<script setup>
/**
 * The live customer card (P-2.12): who the owner is pretending to be, beside the chat.
 *
 * Replaces the collapsed "pretend to be a particular customer" form. The card is the contact
 * every message sends; after each reply it shows what the assistant saved or changed about the
 * customer, marked "Just changed" until the next turn, and a save the real system would refuse
 * struck through with the reason. Every field can be edited at any time, "Reset customer"
 * empties it, and one line always says none of it reaches anybody.
 *
 * It owns nothing: the page holds the card (`helpers/customer-card.js`) and replaces it whole.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloInput from './WasloInput.vue';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** `{ name, email, phone, attributes: [{ key, value }], labels: [] }`. */
  card: { type: Object, required: true },
  /** Fields the last reply's saves changed: `name`, `email`, `phone`, a detail's key, `labels`. */
  changed: { type: Array, default: () => [] },
  /** Saves the real system would refuse: `[{ field, value, reason }]`. */
  refused: { type: Array, default: () => [] },
  /** The task's answers so far: `[{ key, label, question }]`. */
  answers: { type: Array, default: () => [] },
  /** A rejection that names one of these boxes (`contact.*`). */
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:card', 'reset']);

const { t } = useWasloI18n();
const key = name => `WASLO.TEST.CARD.${name}`;

const set = (field, value) =>
  emit('update:card', { ...props.card, [field]: value });
const setAttribute = (index, field, value) =>
  set(
    'attributes',
    props.card.attributes.map((row, at) =>
      at === index ? { ...row, [field]: value } : row
    )
  );
const addAttribute = () =>
  set('attributes', [...props.card.attributes, { key: '', value: '' }]);
const removeAttribute = index =>
  set(
    'attributes',
    props.card.attributes.filter((_, at) => at !== index)
  );

const newLabel = ref('');
const addLabel = () => {
  const label = newLabel.value.trim();
  newLabel.value = '';
  if (label === '' || props.card.labels.includes(label)) return;
  set('labels', [...props.card.labels, label]);
};
const removeLabel = label =>
  set(
    'labels',
    props.card.labels.filter(l => l !== label)
  );

const isChanged = field => props.changed.includes(field);

/** "Would not be saved: …", in the words of the one reason the card can know. */
const REFUSED_KEY = { email: 'REFUSED_EMAIL', phone: 'REFUSED_PHONE' };
const refusals = computed(() =>
  props.refused.map(r => ({
    ...r,
    sentence: t(key(REFUSED_KEY[r.field] ?? 'REFUSED_OTHER')),
  }))
);

const FIELDS = [
  { field: 'name', label: 'NAME', placeholder: 'NAME_PLACEHOLDER' },
  { field: 'phone', label: 'PHONE', placeholder: 'PHONE_PLACEHOLDER' },
  { field: 'email', label: 'EMAIL', placeholder: 'EMAIL_PLACEHOLDER' },
];
</script>

<template>
  <section
    class="flex flex-col gap-4 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
    data-waslo-test-card
  >
    <div class="flex items-center justify-between gap-3">
      <h3 class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </h3>
      <Button
        type="button"
        :label="t(key('RESET'))"
        variant="ghost"
        color="slate"
        size="xs"
        data-waslo-test-card-reset
        @click="emit('reset')"
      />
    </div>
    <p class="mb-0 text-xs text-n-slate-11" data-waslo-test-card-note>
      {{ t(key('NOTE')) }}
    </p>

    <div
      v-for="row in FIELDS"
      :key="row.field"
      class="flex flex-col gap-1"
      :data-waslo-test-card-field="row.field"
    >
      <WasloInput
        :model-value="card[row.field]"
        :label="t(`WASLO.TEST.CUSTOMER.${row.label}`)"
        :placeholder="t(`WASLO.TEST.CUSTOMER.${row.placeholder}`)"
        @update:model-value="value => set(row.field, value)"
      />
      <span
        v-if="isChanged(row.field)"
        class="self-start px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
        data-waslo-test-card-changed
      >
        {{ t(key('JUST_CHANGED')) }}
      </span>
    </div>

    <ul v-if="refusals.length" class="flex flex-col gap-1 mb-0">
      <li
        v-for="(refusal, index) in refusals"
        :key="`${refusal.field}-${index}`"
        class="flex flex-col text-xs"
        data-waslo-test-card-refused
      >
        <bdi class="line-through text-n-slate-10">{{ refusal.value }}</bdi>
        <span class="text-n-amber-11">{{ refusal.sentence }}</span>
      </li>
    </ul>

    <div class="flex flex-col gap-2" data-waslo-test-card-details>
      <p class="mb-0 text-xs font-medium text-n-slate-11">
        {{ t('WASLO.TEST.CUSTOMER.ATTRS') }}
      </p>
      <div
        v-for="(row, index) in card.attributes"
        :key="index"
        class="flex items-end gap-2"
        data-waslo-test-card-detail
      >
        <WasloInput
          :model-value="row.key"
          :placeholder="t('WASLO.TEST.CUSTOMER.ATTR_KEY_PLACEHOLDER')"
          @update:model-value="value => setAttribute(index, 'key', value)"
        />
        <WasloInput
          :model-value="row.value"
          :placeholder="t('WASLO.TEST.CUSTOMER.ATTR_VALUE_PLACEHOLDER')"
          @update:model-value="value => setAttribute(index, 'value', value)"
        />
        <span
          v-if="row.key && isChanged(row.key)"
          class="px-2 py-0.5 mb-2 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
          data-waslo-test-card-changed
        >
          {{ t(key('JUST_CHANGED')) }}
        </span>
        <button
          type="button"
          class="mb-2 text-xs underline text-n-slate-11"
          data-waslo-test-card-detail-remove
          @click="removeAttribute(index)"
        >
          {{ t(key('REMOVE')) }}
        </button>
      </div>
      <button
        type="button"
        class="self-start text-xs underline text-n-blue-11"
        data-waslo-test-card-detail-add
        @click="addAttribute"
      >
        {{ t(key('ADD_DETAIL')) }}
      </button>
    </div>

    <div class="flex flex-col gap-2" data-waslo-test-card-labels>
      <p class="mb-0 text-xs font-medium text-n-slate-11">
        {{ t(key('LABELS')) }}
        <span
          v-if="isChanged('labels')"
          class="px-2 py-0.5 ms-1 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
          data-waslo-test-card-changed
        >
          {{ t(key('JUST_CHANGED')) }}
        </span>
      </p>
      <div v-if="card.labels.length" class="flex flex-wrap gap-1">
        <span
          v-for="label in card.labels"
          :key="label"
          class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-lg bg-n-alpha-2 text-n-slate-12"
          data-waslo-test-card-label
        >
          <bdi>{{ label }}</bdi>
          <button
            type="button"
            :aria-label="t(key('REMOVE_LABEL'), { label })"
            @click="removeLabel(label)"
          >
            <span class="size-3 i-lucide-x" aria-hidden="true" />
          </button>
        </span>
      </div>
      <WasloInput
        v-model="newLabel"
        :placeholder="t(key('LABEL_PLACEHOLDER'))"
        data-waslo-test-card-label-input
        @keydown.enter.prevent="addLabel"
      />
    </div>

    <div
      v-if="answers.length"
      class="flex flex-col gap-1"
      data-waslo-test-card-answers
    >
      <p class="mb-0 text-xs font-medium text-n-slate-11">
        {{ t(key('ANSWERS')) }}
      </p>
      <p
        v-for="answer in answers"
        :key="answer.key"
        class="flex flex-wrap gap-x-2 mb-0 text-xs"
        data-waslo-test-card-answer
      >
        <bdi class="text-n-slate-11">{{ answer.question ?? answer.key }}</bdi>
        <bdi class="text-n-slate-12">{{ answer.label }}</bdi>
      </p>
    </div>

    <p v-if="message" class="mb-0 text-sm text-n-ruby-9" role="alert">
      {{ message }}
    </p>
  </section>
</template>
